﻿import type { FlowAnswers } from "../flow/steps";
import type { UserApplication } from "../domain/types";
import type { ProfileAnswers } from "../domain/answers";
import {
  effectiveReviewStatus as resolveEffectiveReviewStatus,
  isGuestUserId,
  isModerationBlocking,
  isReviewApprovedForDeck,
} from "./approvalGate";
import { DEMO_PROFILES } from "./demoPool";
import { GUEST_ANSWERS } from "./demoAnswers";
import { pairAlignmentScore } from "./engine";
import {
  actedTargetIds,
  matchedPartnerIds,
  otherUserId,
} from "./interestEngine";
import { interestStore } from "./interestStore";
import { applyReviewDecisionToPool, matchingPoolStore } from "./poolStore";
import type { Gender, MatchProfile, MatchRecord, MatchState } from "./types";
import { oppositeGender, scalesFromAnswers } from "./types";
import { WEEKLY_SUGGESTION_CAP } from "./weights";
import {
  clearWeeklyForUser,
  daysUntilNextSelection,
  ensureWeeklySelection,
  getWeeklyRecord,
  markWeeklyLiked,
  markWeeklyPassed,
  openWeeklyIds,
  presentedWeeklyIds,
  visibleWeeklyIds,
  refreshWeeklySelection,
  spendExtraReveal as spendWeeklyExtraReveal,
  weekKey,
  type WeeklyRecord,
} from "./weeklyStore";
import { writeLocal } from "../storage/localWrite";
import {
  expressOnServer,
  fetchServerDeck,
  fetchServerMatches,
  isServerMode,
  sendMessage as sendMessageServer,
  serverAvailable,
} from "../api/backend";
import { billingStore } from "../billing/billingStore";

const STORAGE_KEY = "un8.matching.session.v1";
const LEGACY_STORAGE_KEY = "un8.matching.v1";

/** In-memory server deck (approved profiles from /api/deck). */
let serverDeckProfiles: MatchProfile[] = [];
let serverDeckReason: string | null = null;
/** From /api/deck — pending_review / needs_resubmit / approved / etc. */
let serverReviewStatus: string | null = null;
/** True when we are showing the local demo deck because the live deck is held. */
let usingDemoFallback = false;
let serverMatchProfiles = new Map<string, MatchProfile>();

function isAuthGateReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return /sign in required|sign in to use|sign in first|unauthorized/i.test(reason);
}

function hasSignedInMemberSession(): boolean {
  const session = readSession();
  return Boolean(session.self && !isGuestUserId(session.self.id));
}

function isModerationHoldReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return /moderat|approval|pending|waiting for|complete and submit/i.test(reason);
}

function isBillingPaywallReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return /subscribe|see new people|free trial|keep chatting|keep everyone|who liked|extra reveal/i.test(
    reason
  );
}

/**
 * Apply local applications/review approval onto in-memory server hold flags.
 * Never invents approval when local status is missing.
 * Guests never receive a moderation hold (no ID / no case).
 */
function reconcileApprovalHold(selfId: string | null | undefined): void {
  if (!selfId) return;
  if (isGuestUserId(selfId)) {
    serverReviewStatus = null;
    if (isModerationHoldReason(serverDeckReason)) serverDeckReason = null;
    return;
  }
  if (isReviewApprovedForDeck(selfId, serverReviewStatus)) {
    serverReviewStatus = "approved";
    if (isModerationHoldReason(serverDeckReason)) {
      serverDeckReason = null;
    }
    return;
  }
  const effective = resolveEffectiveReviewStatus(selfId, serverReviewStatus);
  if (effective && effective !== "approved") {
    serverReviewStatus = effective;
    if (!serverDeckReason || !isModerationHoldReason(serverDeckReason)) {
      serverDeckReason =
        "Your profile is waiting for moderation approval before the deck opens.";
    }
  }
}

/**
 * Pending moderation, missing profile, empty live pool, or legacy billing
 * “new people” reason → still show weekly four from demo (Kirk: free gets four).
 */
function shouldFallbackToDemoDeck(selfId?: string | null): boolean {
  if (serverDeckProfiles.length > 0) return false;
  if (selfId && isReviewApprovedForDeck(selfId, serverReviewStatus)) {
    // Approved — only fall back when the live deck is empty (thin pool).
    return serverDeckProfiles.length === 0;
  }
  if (serverReviewStatus && serverReviewStatus !== "approved") return true;
  if (isModerationHoldReason(serverDeckReason)) return true;
  // Billing copy used to blank the deck; weekly four stay available on free.
  if (isBillingPaywallReason(serverDeckReason)) return true;
  return true;
}

let lastSessionWriteFailed = false;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

// Bubble interestStore changes to match UI subscribers
interestStore.subscribe(() => notify());

function todayKey(): string {
  return weekKey();
}

interface SessionState {
  self: MatchProfile | null;
  deckDate: string;
  deckIds: string[];
  /** Migration flag */
  interestsMigrated?: boolean;
}

function emptySession(): SessionState {
  return { self: null, deckDate: "", deckIds: [], interestsMigrated: false };
}

function readSession(): SessionState {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    // One-time pull from legacy combined store
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        const parsed = JSON.parse(legacy) as MatchState & {
          livePool?: MatchProfile[];
          interestsMigrated?: boolean;
        };
        if (Array.isArray(parsed.livePool)) {
          for (const p of parsed.livePool) {
            if (p?.id && !matchingPoolStore.has(p.id)) {
              matchingPoolStore.upsertProfile(p, p.id);
            }
          }
        }
        if (parsed.self && !parsed.interestsMigrated) {
          interestStore.migrateFromLegacySession({
            selfId: parsed.self.id,
            likedIds: parsed.likedIds ?? [],
            passedIds: parsed.passedIds ?? [],
            matches: parsed.matches ?? [],
          });
        }
        const session: SessionState = {
          self: parsed.self ?? null,
          deckDate: parsed.deckDate ?? "",
          deckIds: parsed.deckIds ?? [],
          interestsMigrated: true,
        };
        writeSession(session);
        return session;
      }
      return emptySession();
    }
    return { ...emptySession(), ...(JSON.parse(raw) as SessionState) };
  } catch {
    return emptySession();
  }
}

function sessionSnapshot(state: SessionState): string {
  return JSON.stringify(state);
}

function writeSession(state: SessionState, opts?: { forceNotify?: boolean }) {
  const next = sessionSnapshot(state);
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(STORAGE_KEY);
  } catch {
    prev = null;
  }
  if (prev === next && !opts?.forceNotify) {
    // Same bytes — do not notify. Notifying on a no-op write re-entered React
    // during render (getState → ensureDeck) and threw minified #301.
    return;
  }
  const result = writeLocal(STORAGE_KEY, next);
  lastSessionWriteFailed = !result.ok;
  notify();
}

function deriveState(session: SessionState): MatchState {
  const self = session.self;
  if (!self) {
    return {
      self: null,
      likedIds: [],
      passedIds: [],
      matches: [],
      deckDate: session.deckDate,
      deckIds: session.deckIds,
    };
  }

  const interests = interestStore.listInterests();
  const likedIds: string[] = [];
  const passedIds: string[] = [];
  for (const i of interests) {
    if (i.fromUserId !== self.id) continue;
    if (i.status === "liked") likedIds.push(i.toUserId);
    if (i.status === "passed") passedIds.push(i.toUserId);
  }

  const matches: MatchRecord[] = interestStore.matchesFor(self.id).map((m) => ({
    id: m.id,
    profileId: otherUserId(m, self.id),
    matchedAt: m.createdAt,
    valuesScore: m.valuesScore,
    chatUnlocked: m.chatUnlocked === true,
  }));

  return {
    self,
    likedIds,
    passedIds,
    matches,
    deckDate: session.deckDate,
    deckIds: session.deckIds,
  };
}

function excludedIds(selfId: string): Set<string> {
  const interests = interestStore.listInterests();
  const matches = interestStore.listMatches();
  const set = actedTargetIds(interests, selfId);
  for (const id of matchedPartnerIds(matches, selfId)) set.add(id);
  set.add(selfId);
  for (const id of presentedWeeklyIds(getWeeklyRecord(selfId))) set.add(id);
  return set;
}

/** Inbound likers — soft preference when building the weekly four. */
function inboundLikedIds(selfId: string): Set<string> {
  const set = new Set<string>();
  for (const i of interestStore.listInterests()) {
    if (i.toUserId === selfId && i.status === "liked") set.add(i.fromUserId);
  }
  return set;
}

/**
 * Seed demo reverse-likes into interestStore so mutual matches are real edges.
 * Idempotent; only for profiles with likedYou flag.
 */
function seedDemoInboundLikes(selfId: string, profiles: MatchProfile[]) {
  for (const p of profiles) {
    if (p.likedYou) {
      interestStore.ensureLike(p.id, selfId);
    }
  }
}

/** Seed demo reverse-likes without going through getState (safe outside render). */
function seedInboundForSelf(self: MatchProfile) {
  const want = oppositeGender(self.gender);
  seedDemoInboundLikes(
    self.id,
    getCandidatePool().filter((p) => p.gender === want)
  );
}

export function getCandidatePool(): MatchProfile[] {
  const pool = matchingPoolStore.listProfiles();
  const poolIds = new Set(pool.map((p) => p.id));
  const demo = DEMO_PROFILES.filter((p) => !poolIds.has(p.id)).map((p) => ({
    ...p,
    source: "demo" as const,
  }));
  if (pool.length === 0) return demo;
  return [...pool, ...demo];
}

export function getActivePool(): {
  profiles: MatchProfile[];
  source: "live" | "demo" | "mixed";
} {
  // Ensure closed-test always has opposite-sex candidates beyond self.
  if (matchingPoolStore.size() < 4) {
    matchingPoolStore.seedDemoProfiles();
  }
  const pool = matchingPoolStore.listProfiles();
  if (pool.length === 0) return { profiles: DEMO_PROFILES, source: "demo" };
  const profiles = getCandidatePool();
  const hasDemo = profiles.some((p) => p.source === "demo");
  return { profiles, source: hasDemo ? "mixed" : "live" };
}

function deckIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function ensureDeck(session: SessionState): SessionState {
  if (!session.self) return session;
  const key = todayKey();
  const billing = billingStore.get();
  if (matchingPoolStore.size() < WEEKLY_SUGGESTION_CAP) {
    matchingPoolStore.seedDemoProfiles();
  }
  const { profiles } = getActivePool();
  // Do not seed interests here — ensureLike notifies and would setState during
  // MatchApp/MatchFeed render via getState(). Seed from enter* / seedInbound().
  const weekly = ensureWeeklySelection({
    self: session.self,
    pool: profiles,
    excludedIds: excludedIds(session.self.id),
    preferIds: inboundLikedIds(session.self.id),
    isSubscriber:
        billing.plan === "subscriber" ||
        billing.plan === "vip" ||
        billing.canUseExtraReveals,
  });
  const deckIds = presentedWeeklyIds(weekly);
  // Empty weekly selection is a valid settled state (moderation hold / thin pool).
  // Requiring deckIds.length > 0 re-wrote + notified on every getState() render → #301.
  if (session.deckDate === key && session.deckDate === weekly.weekKey && deckIdsEqual(session.deckIds, deckIds)) {
    return session;
  }
  const next = { ...session, deckDate: key, deckIds };
  writeSession(next);
  return next;
}

function invalidateDeck(session: SessionState): SessionState {
  return { ...session, deckDate: "", deckIds: [] };
}

export function profileFromAnswers(
  answers: FlowAnswers,
  userId: string,
  profileAnswers?: ProfileAnswers
): MatchProfile | null {
  if (answers.gender !== "male" && answers.gender !== "female") return null;
  const age = Number(answers.age);
  if (!answers.firstName.trim() || !(age >= 18)) return null;
  if (!userId) return null;
  return {
    id: userId,
    firstName: answers.firstName.trim(),
    age,
    city: answers.city.trim() || "—",
    country: answers.country?.trim().toUpperCase() || undefined,
    preferredPartnerCountry: answers.preferredPartnerCountry || "any",
    gender: answers.gender,
    profession: answers.profession.trim() || "—",
    aboutYou: answers.aboutYou.trim() || "A serious Christian seeking covenant.",
    lookingFor: answers.lookingFor.trim() || "Faith, honesty, and lasting commitment.",
    hobbies: answers.hobbies.trim() || "—",
    scales: scalesFromAnswers(answers.scales),
    source: "self",
    ...(profileAnswers ? { answers: profileAnswers } : {}),
  };
}

export function guestSelf(gender: Gender, userId: string): MatchProfile {
  return {
    id: userId,
    firstName: "You",
    age: 30,
    city: "Your city",
    country: "US",
    preferredPartnerCountry: "any",
    gender,
    profession: "Member",
    aboutYou: "Guest session — complete onboarding for a full profile.",
    lookingFor: "A serious Christian partner.",
    hobbies: "—",
    scales: { loyalty: 8, honesty: 8, passion: 7, commitment: 8 },
    source: "self",
    // Demo only: gives the guest a questionnaire so the two-stage model
    // actually runs. Real members get theirs from onboarding.
    answers: GUEST_ANSWERS,
  };
}

export const matchStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  getState(): MatchState {
    const session = ensureDeck(readSession());
    return deriveState(session);
  },

  listLivePool(): MatchProfile[] {
    return matchingPoolStore.listProfiles();
  },

  poolSource(): "live" | "demo" | "mixed" {
    return getActivePool().source;
  },

  poolSize(): number {
    return matchingPoolStore.size();
  },

  sessionWriteFailed(): boolean {
    return lastSessionWriteFailed;
  },

  invalidateDailyDeck(): void {
    writeSession(invalidateDeck(readSession()));
  },

  syncFromApplication(app: UserApplication): MatchProfile | null {
    const result = applyReviewDecisionToPool(app, app.verification.status);
    // Closed-test: keep opposite-sex demo people in the pool so weekly four has candidates.
    matchingPoolStore.seedDemoProfiles();
    const session = readSession();
    if (session.self?.id === (app.userId || app.id)) {
      reconcileApprovalHold(session.self.id);
      if (app.verification.status === "approved") {
        clearWeeklyForUser(session.self.id);
      }
    }
    this.invalidateDailyDeck();
    if (result.ok && "entry" in result) return result.entry.profile;
    return matchingPoolStore.getProfile(app.userId || app.id);
  },

  /** After review approval — clear stale server hold when local/server says approved. */
  acknowledgeApproval(userId: string): void {
    matchingPoolStore.seedDemoProfiles();
    const session = readSession();
    if (session.self?.id === userId) {
      reconcileApprovalHold(userId);
      if (isReviewApprovedForDeck(userId, serverReviewStatus)) {
        clearWeeklyForUser(userId);
        writeSession(invalidateDeck(session));
      }
    }
    notify();
  },

  /** Seed DEMO_PROFILES into the matching pool and rebuild this week's selection. */
  seedDemoPool(): number {
    const added = matchingPoolStore.seedDemoProfiles();
    const session = readSession();
    if (session.self) {
      clearWeeklyForUser(session.self.id);
      writeSession(invalidateDeck(session));
    } else {
      notify();
    }
    return added;
  },

  enterFromOnboarding(
    answers: FlowAnswers,
    userId: string,
    profileAnswers?: ProfileAnswers
  ): MatchProfile | null {
    const self = profileFromAnswers(answers, userId, profileAnswers);
    if (!self) return null;
    seedInboundForSelf(self);
    const prev = readSession();
    writeSession(
      ensureDeck({ ...prev, self, deckDate: "", deckIds: [], interestsMigrated: true })
    );
    return self;
  },

  /** Bind matching session to an authenticated user id. */
  enterAsUser(self: MatchProfile): void {
    matchingPoolStore.seedDemoProfiles();
    seedInboundForSelf(self);
    writeSession(
      ensureDeck({
        self,
        deckDate: "",
        deckIds: [],
        interestsMigrated: true,
      })
    );
  },

  enterAsGuest(gender: Gender, userId: string): void {
    // Guests never inherit a prior member's moderation / empty-server hold.
    serverDeckReason = null;
    serverReviewStatus = null;
    serverDeckProfiles = [];
    usingDemoFallback = true;
    matchingPoolStore.seedDemoProfiles();
    const self = guestSelf(gender, userId);
    seedInboundForSelf(self);
    clearWeeklyForUser(self.id);
    writeSession(
      ensureDeck({
        self,
        deckDate: "",
        deckIds: [],
        interestsMigrated: true,
      })
    );
  },

  /** Drop matching session (logout). Does not clear the shared pool. */
  clearSession(): void {
    serverDeckReason = null;
    serverReviewStatus = null;
    serverDeckProfiles = [];
    usingDemoFallback = false;
    writeSession(emptySession());
  },

  /** Safe to call from effects — seeds demo inbound likes without render-time notify storms. */
  seedInbound(): void {
    const self = readSession().self;
    if (self) seedInboundForSelf(self);
  },

  getProfile(id: string): MatchProfile | null {
    const state = this.getState();
    if (state.self?.id === id) return state.self;
    return (
      serverMatchProfiles.get(id) ??
      serverDeckProfiles.find((p) => p.id === id) ??
      matchingPoolStore.getProfile(id) ??
      DEMO_PROFILES.find((p) => p.id === id) ??
      null
    );
  },

  getDailyQueue(): MatchProfile[] {
    const session = readSession();
    if (!session.self) return [];

    usingDemoFallback = false;
    reconcileApprovalHold(session.self.id);

    // Guests always use the demo/mixed weekly selection — never moderation or paywall.
    const isGuest = isGuestUserId(session.self.id);
    if (isGuest) {
      serverDeckReason = null;
      serverReviewStatus = null;
      matchingPoolStore.seedDemoProfiles();
      usingDemoFallback = true;
    } else {
      const billing = billingStore.get();
      const demoHold = shouldFallbackToDemoDeck(session.self.id);
      const moderationBlocks = isModerationBlocking(
        session.self.id,
        serverReviewStatus,
        serverDeckReason
      );

      // Kirk: weekly four stay on free. Only blank if signed-out empty billing
      // (canBrowseWeeklyFour false) and not a moderation/demo hold.
      if (!billing.canBrowseWeeklyFour && !demoHold && !moderationBlocks) {
        if (!isServerMode() || serverDeckProfiles.length === 0) {
          if (!hasSignedInMemberSession()) {
            serverDeckReason = billing.copy;
            if (!isServerMode()) return [];
          }
        }
      }

      if (isServerMode() && demoHold) {
        usingDemoFallback = true;
      } else if (isServerMode() && serverDeckProfiles.length === 0) {
        usingDemoFallback = true;
      }

      // Approved locally/server: do not surface a stale moderation empty-state reason.
      if (isReviewApprovedForDeck(session.self.id, serverReviewStatus)) {
        if (isModerationHoldReason(serverDeckReason)) serverDeckReason = null;
        // Thin live deck → same mixed pool guests use (seed demos).
        if (serverDeckProfiles.length === 0) {
          matchingPoolStore.seedDemoProfiles();
          usingDemoFallback = true;
        }
      }
    }

    // Seed before ensureDeck (getState) so the first weekly write sees candidates.
    if (matchingPoolStore.size() < WEEKLY_SUGGESTION_CAP) {
      matchingPoolStore.seedDemoProfiles();
    }

    const state = this.getState();
    const self = state.self;
    if (!self) return [];

    const billing = billingStore.get();
    const { profiles } = getActivePool();
    const weekly = ensureWeeklySelection({
      self,
      pool: profiles,
      excludedIds: excludedIds(self.id),
      preferIds: inboundLikedIds(self.id),
      isSubscriber:
        billing.plan === "subscriber" ||
        billing.plan === "vip" ||
        billing.canUseExtraReveals,
    });
    // Sync session deckIds for empty-state / legacy readers (no-op write if unchanged)
    const sessionNow = readSession();
    const presented = presentedWeeklyIds(weekly);
    if (
      sessionNow.self?.id === self.id &&
      (sessionNow.deckDate !== weekly.weekKey || !deckIdsEqual(sessionNow.deckIds, presented))
    ) {
      writeSession({ ...sessionNow, deckDate: weekly.weekKey, deckIds: presented });
    }

    const byId = new Map(getCandidatePool().map((p) => [p.id, p]));
    return visibleWeeklyIds(weekly)
      .map((id) => byId.get(id) ?? this.getProfile(id))
      .filter((p): p is MatchProfile => p != null && p.id !== self.id);
  },

  async syncFromServer(): Promise<void> {
    if (!(await serverAvailable())) return;
    const session = readSession();
    // Guests stay on the local demo deck — never overwrite with an empty server deck.
    if (session.self && isGuestUserId(session.self.id)) {
      matchingPoolStore.seedDemoProfiles();
      usingDemoFallback = true;
      serverDeckReason = null;
      serverReviewStatus = null;
      writeSession(ensureDeck({ ...session, deckDate: "", deckIds: [] }));
      return;
    }
    const deck = await fetchServerDeck();
    if (isAuthGateReason(deck.reason) && hasSignedInMemberSession()) {
      // Locale change or transient cookie must not pause matching for a signed-in member.
      if (deck.profiles.length > 0) serverDeckProfiles = deck.profiles;
      if (isAuthGateReason(serverDeckReason)) serverDeckReason = null;
    } else {
      serverDeckProfiles = deck.profiles;
      serverDeckReason = deck.reason ?? null;
    }
    serverReviewStatus = deck.reviewStatus ?? serverReviewStatus;
    reconcileApprovalHold(session.self?.id);
    const selfId = session.self?.id;
    const matches = await fetchServerMatches();
    for (const m of matches) {
      serverMatchProfiles.set(m.profile.id, m.profile);
      if (selfId) {
        const userIds: [string, string] =
          selfId < m.profile.id ? [selfId, m.profile.id] : [m.profile.id, selfId];
        interestStore.importServerMatch({
          matchId: m.record.id,
          userIds,
          createdAt: m.record.matchedAt,
          valuesScore: m.record.valuesScore,
        });
      }
    }
    if (session.self) {
      if (serverDeckProfiles.length > 0) {
        writeSession({
          ...session,
          deckDate: todayKey(),
          deckIds: serverDeckProfiles.map((p) => p.id),
        });
      } else if (shouldFallbackToDemoDeck(session.self.id)) {
        // Keep a usable demo deck while moderation (or missing profile) holds the live one.
        matchingPoolStore.seedDemoProfiles();
        usingDemoFallback = true;
        writeSession(ensureDeck({ ...session, deckDate: "", deckIds: [] }));
      } else {
        matchingPoolStore.seedDemoProfiles();
        usingDemoFallback = true;
        writeSession(ensureDeck({ ...session, deckDate: "", deckIds: [] }));
      }
    } else {
      notify();
    }
  },

  serverDeckReason(): string | null {
    return serverDeckReason;
  },

  /** True when live deck is held and we are serving demo cards instead. */
  usingDemoFallback(): boolean {
    const selfId = readSession().self?.id;
    return usingDemoFallback || shouldFallbackToDemoDeck(selfId);
  },

  /** Effective review status after reconciling local applications with server. */
  effectiveReviewStatus(): string | null {
    const selfId = readSession().self?.id;
    if (!selfId) return serverReviewStatus;
    return resolveEffectiveReviewStatus(selfId, serverReviewStatus);
  },

  serverReviewStatus(): string | null {
    return serverReviewStatus;
  },

  pass(profileId: string): void {
    const state = this.getState();
    if (!state.self) return;
    if (interestStore.hasLiked(state.self.id, profileId)) return;
    if (interestStore.hasPassed(state.self.id, profileId)) return;
    interestStore.express(state.self.id, profileId, "passed");
    markWeeklyPassed(state.self.id, profileId);
    // Immediate 24h replace when pool allows
    const billing = billingStore.get();
    const { profiles } = getActivePool();
    ensureWeeklySelection({
      self: state.self,
      pool: profiles,
      excludedIds: excludedIds(state.self.id),
      preferIds: inboundLikedIds(state.self.id),
      isSubscriber:
        billing.plan === "subscriber" ||
        billing.plan === "vip" ||
        billing.canUseExtraReveals,
    });
    void expressOnServer(profileId, "passed");
    notify();
  },

  /**
   * Like a profile. Mutual match only if the other user already liked self
   * (persisted counter-like in interestStore — including seeded demo inbound likes).
   * In server mode, waits for /api/likes so a cross-device mutual is detected.
   */
  async like(profileId: string): Promise<MatchRecord | null> {
    const state = this.getState();
    if (!state.self) return null;

    const profile = this.getProfile(profileId);
    if (!profile || profile.id === state.self.id) return null;

    // Seed demo reverse-like before express so mutual is real edge-based
    if (profile.likedYou) {
      interestStore.ensureLike(profile.id, state.self.id);
    }

    if (interestStore.hasLiked(state.self.id, profileId)) {
      const existing = interestStore
        .matchesFor(state.self.id)
        .find((m) => otherUserId(m, state.self!.id) === profileId);
      if (existing) {
        return {
          id: existing.id,
          profileId,
          matchedAt: existing.createdAt,
          valuesScore: existing.valuesScore,
          chatUnlocked: Boolean(existing.chatUnlocked),
        };
      }
      return null;
    }

    const score = pairAlignmentScore(state.self, profile);
    const result = interestStore.express(
      state.self.id,
      profileId,
      "liked",
      score
    );
    markWeeklyLiked(state.self.id, profileId);

    if (await serverAvailable()) {
      const remote = await expressOnServer(profileId, "liked", score);
      if (remote.match) {
        interestStore.importServerMatch({
          matchId: remote.match.id,
          userIds:
            state.self.id < profileId
              ? [state.self.id, profileId]
              : [profileId, state.self.id],
          createdAt: remote.match.matchedAt,
          valuesScore: remote.match.valuesScore,
        });
        notify();
        return remote.match;
      }
      return null;
    }

    if (!result.mutual || !result.match) return null;

    return {
      id: result.match.id,
      profileId,
      matchedAt: result.match.createdAt,
      valuesScore: result.match.valuesScore,
      chatUnlocked: result.match.chatUnlocked === true,
    };
  },

  listMatches(): MatchRecord[] {
    return this.getState().matches;
  },

  /** Persistent outbound likes for current self. */
  likedIds(): string[] {
    return this.getState().likedIds.slice();
  },

  getMatch(matchId: string): MatchRecord | null {
    const self = this.getState().self;
    if (!self) return null;
    const m = interestStore.getMatch(matchId);
    if (!m || m.status !== "active") return null;
    if (m.userIds[0] !== self.id && m.userIds[1] !== self.id) return null;
    return {
      id: m.id,
      profileId: otherUserId(m, self.id),
      matchedAt: m.createdAt,
      valuesScore: m.valuesScore,
      chatUnlocked: m.chatUnlocked === true,
    };
  },

  unlockChat(matchId: string): MatchRecord | null {
    const unlocked = interestStore.unlockChat(matchId);
    if (!unlocked) return null;
    return this.getMatch(matchId);
  },

  listMessages(matchId: string) {
    return interestStore.listMessages(matchId);
  },

  async pullMessages(matchId: string): Promise<void> {
    const { fetchMessages } = await import("../api/backend");
    const remote = await fetchMessages(matchId);
    interestStore.mergeRemoteMessages(remote);
  },

  async sendMessage(
    matchId: string,
    body: string
  ): Promise<
    | { ok: true }
    | { ok: false; error: string; reason?: string; needsId?: boolean; needsVideo?: boolean }
    | null
  > {
    const self = this.getState().self;
    if (!self) return null;

    // Verify before you can message (11 Sep 2026). Browsing and hearting stay
    // open; a conversation needs ID + confirmation video. See verify/verifyGate.
    const { canMessage } = await import("../verify/verifyGate");
    const verdict = canMessage(self.id);
    if (!verdict.ok) return verdict;

    const record = this.getMatch(matchId);
    if (!record) return { ok: false, error: "Match not found." };

    // One new outbound conversation start per ISO week; existing threads free.
    const prior = interestStore.listMessages(matchId);
    const alreadyMessaged = prior.some((m) => m.fromUserId === self.id);
    if (!alreadyMessaged) {
      const { newMessageWeekStore } = await import("./newMessageWeekStore");
      const gate = newMessageWeekStore.recordStart(self.id, record.profileId);
      if (!gate.ok) return gate;
    }

    const { isServerMode } = await import("../api/backend");

    if (isServerMode()) {
      const remote = await sendMessageServer(matchId, body);
      if (!remote.ok) {
        return { ok: false, error: remote.error };
      }
      interestStore.mergeRemoteMessages([remote.message]);
      return { ok: true };
    }

    const local = interestStore.sendMessage(matchId, self.id, body);
    if (!local) return { ok: false, error: "Chat is not unlocked for this match." };
    return { ok: true };
  },

  /**
   * Credit-gated refresh of the weekly four (free switch / subscriber 5/week /
   * VIP unlimited). Call after billingStore.tryDeckSwap succeeds, or let this
   * method run the billing check itself.
   */
  swapWeeklyFour(): { ok: true } | { ok: false; error: string } {
    const self = readSession().self;
    if (!self) return { ok: false, error: "No profile." };
    const pay = billingStore.tryDeckSwap(self.id);
    if (!pay.ok) return pay;

    const { profiles } = getActivePool();
    const billing = billingStore.get();
    refreshWeeklySelection({
      self,
      pool: profiles,
      excludedIds: excludedIds(self.id),
      preferIds: inboundLikedIds(self.id),
      isSubscriber:
        billing.plan === "subscriber" ||
        billing.plan === "vip" ||
        billing.canUseExtraReveals,
    });
    const session = readSession();
    if (session.self) {
      writeSession(
        ensureDeck({
          ...session,
          deckDate: "",
          deckIds: [],
        })
      );
    }
    notify();
    return { ok: true };
  },

  scoreAgainst(self: MatchProfile, other: MatchProfile): number {
    return pairAlignmentScore(self, other);
  },

  /** Whether the other user has already liked the current self (for UI badges). */
  theyLikedYou(profileId: string): boolean {
    const self = readSession().self;
    if (!self) return false;
    const billing = billingStore.get();
    if (!billing.canSeeWhoLikedYou && !self.id.startsWith("user_guest_")) {
      return false;
    }
    return interestStore.hasLiked(profileId, self.id);
  },

  getWeeklyMeta(): {
    weekKey: string;
    daysUntilNext: number;
    thinPool: boolean;
    presentedCount: number;
    openCount: number;
    targetCount: number;
    extraRevealsRemaining: number;
    record: WeeklyRecord | null;
  } {
    const self = readSession().self;
    if (!self) {
      return {
        weekKey: weekKey(),
        daysUntilNext: daysUntilNextSelection(),
        thinPool: false,
        presentedCount: 0,
        openCount: 0,
        targetCount: WEEKLY_SUGGESTION_CAP,
        extraRevealsRemaining: 0,
        record: null,
      };
    }
    const billing = billingStore.get();
    const { profiles } = getActivePool();
    const weekly = ensureWeeklySelection({
      self,
      pool: profiles,
      excludedIds: excludedIds(self.id),
      preferIds: inboundLikedIds(self.id),
      isSubscriber:
        billing.plan === "subscriber" ||
        billing.plan === "vip" ||
        billing.canUseExtraReveals,
    });
    return {
      weekKey: weekly.weekKey,
      daysUntilNext: daysUntilNextSelection(),
      thinPool: weekly.thinPool,
      presentedCount: presentedWeeklyIds(weekly).length,
      openCount: openWeeklyIds(weekly).length,
      targetCount: weekly.targetCount,
      extraRevealsRemaining: weekly.extraRevealsRemaining,
      record: weekly,
    };
  },

  spendExtraReveal(): { ok: true } | { ok: false; error: string } {
    const self = readSession().self;
    if (!self) return { ok: false, error: "No profile." };
    const billing = billingStore.get();
    if (!billing.canUseExtraReveals) {
      return { ok: false, error: "Extra reveals are for subscribers." };
    }
    const { profiles } = getActivePool();
    const result = spendWeeklyExtraReveal({
      self,
      pool: profiles,
      excludedIds: excludedIds(self.id),
      preferIds: inboundLikedIds(self.id),
    });
    if (!result.ok) {
      if (result.error === "none_left") return { ok: false, error: "No extra reveals left this week." };
      if (result.error === "no_pool") return { ok: false, error: "No one else fits right now." };
      return { ok: false, error: "Weekly selection not ready." };
    }
    notify();
    return { ok: true };
  },

  /** Unanswered inbound likes (local store). Used for Messages notify / badge. */
  listInboundLikes(): MatchProfile[] {
    const self = readSession().self;
    if (!self) return [];
    const out: MatchProfile[] = [];
    for (const i of interestStore.listInterests()) {
      if (i.toUserId !== self.id || i.status !== "liked") continue;
      if (interestStore.hasLiked(self.id, i.fromUserId)) continue;
      const p = this.getProfile(i.fromUserId);
      if (p && p.gender === oppositeGender(self.gender)) out.push(p);
    }
    return out;
  },

  /** Named inbound likes: paid / guest demo. Free members still get the Messages badge via listInboundLikes. */
  listWhoLikedYou(): MatchProfile[] {
    const self = readSession().self;
    if (!self) return [];
    const billing = billingStore.get();
    const isGuest = self.id.startsWith("user_guest_");
    if (!isGuest && !billing.canSeeWhoLikedYou) return [];
    return this.listInboundLikes();
  },

  resetDemo(): void {
    const session = readSession();
    if (session.self) {
      interestStore.clearForUser(session.self.id);
      clearWeeklyForUser(session.self.id);
      writeSession(
        ensureDeck({
          self: session.self,
          deckDate: "",
          deckIds: [],
          interestsMigrated: true,
        })
      );
      return;
    }
    writeSession(emptySession());
  },
};
